import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { createPortal } from "react-dom";
import "./cost-tooltip.scss";
import { CopyToast } from "../copy-toast/CopyToast";
import { copyToClipboard } from "../../utils/copyToClipboard";

const COPY_TOAST_MS = 1000;

interface Props {
	cost: number;
	onCloseAnimationEnd?: () => void;
}

export const CostTooltip = ({ cost, onCloseAnimationEnd }: Props) => {
	// ---------------- States
	const [copyToastVisible, setCopyToastVisible] = useState(false);
	const hideToastRef = useRef<number | null>(null);

	// COST SUM: always 2 decimals (USD), matching row Cost cells.
	const costLabel = Number(cost).toFixed(2);

	// ---------------- Functions
	const handleClick = async (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		const ok = await copyToClipboard(costLabel);
		if (!ok) return;
		if (hideToastRef.current) window.clearTimeout(hideToastRef.current);
		setCopyToastVisible(true);
		hideToastRef.current = window.setTimeout(() => {
			setCopyToastVisible(false);
			hideToastRef.current = null;
		}, COPY_TOAST_MS);
	};

	// ---------------- Effects
	useEffect(() => {
		return () => {
			if (hideToastRef.current) window.clearTimeout(hideToastRef.current);
		};
	}, []);

	const copiedPreview = `$${costLabel}`;

	return (
		<>
			{createPortal(
				<CopyToast visible={copyToastVisible} text={copiedPreview} />,
				document.body
			)}
			<div
				data-copyable
				data-copy-text={costLabel}
				className='cost-tooltip'
				onClick={handleClick}
				onAnimationEnd={(e) => {
					if (e.animationName !== "cost-tooltip-out") return;
					onCloseAnimationEnd?.();
				}}>
				<div className='cost-tooltip__content'>
					<div className='cost-tooltip__content_title'>${costLabel}</div>
				</div>
			</div>
		</>
	);
};
